import React, { useCallback, useEffect, useMemo, useState } from 'react'

import { getAddressFormatFromAddress } from '@optimex-xyz/trade-wallet-sdk'

import { useGetNetworkIcon } from '@/components/tokens/WalletIcon'
import { Button } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover-v2'
import RenderIf from '@/components/ui/render-if'
import { TANSTACK_QUERY_KEY } from '@/constants/tanstack-query'
import WalletBtcItem from '@/containers/wallet-dropdown/WalletBtcItem'
import WalletItem from '@/containers/wallet-dropdown/WalletItem'
import { queryClient } from '@/context'
import { useCommon, useIsMd, useToast, useWallet } from '@/hooks'
import { CommonArrowDropDown, CommonLogoutNone, CommonWallet } from '@/icons'
import { cn } from '@/lib'
import { useSettings, useWalletModalActions } from '@/store'
import { IToken } from '@/types'
import { WalletContextType } from '@/wallets'

import { useTranslation } from 'react-i18next'

import NavigationHeader from '../histories/NavigationHeader'

interface WalletInfoProps {
  isOpen: boolean
  setOpen: (v: boolean) => void
  token: IToken | null
}

export const WalletInfo: React.FC<WalletInfoProps> = ({ isOpen, setOpen, token }) => {
  const { copyToClipboard, disconnectWallet } = useCommon()
  const { disconnectWallet: disconnect, wallets } = useWallet()
  const { toast } = useToast()
  const [selectedWalletInfo, setSelectedWalletInfo] = useState<WalletContextType>()
  const { setShowWalletModal } = useWalletModalActions()
  const { t } = useTranslation()
  useEffect(() => {
    if (!isOpen) {
      setSelectedWalletInfo(undefined)
    }
  }, [isOpen])

  const onClose = useCallback(() => {
    setOpen(false)
  }, [])

  const connectedWallets = useMemo(() => {
    return Object.entries(wallets).filter(([, wallet]) => wallet.isConnected)
  }, [wallets])

  const handleDisconnect = useCallback(
    async (e: React.MouseEvent, networkId: string) => {
      e.stopPropagation()
      if (await disconnectWallet(networkId)) {
        onClose()
      }
    },
    [disconnectWallet, onClose]
  )

  const handleCopy = useCallback(
    async (address: string) => {
      if (address) {
        await copyToClipboard(address)
      } else {
        console.error('Attempted to copy undefined address')
      }
    },
    [copyToClipboard]
  )

  const handleDisconnectAll = async () => {
    try {
      await disconnect('', true)
      toast({
        title: 'Wallet Disconnected',
        description: 'Your wallet has been successfully disconnected.',
      })
    } catch (error) {
      console.error('Failed to disconnect wallet:', error)
      toast({
        title: 'Disconnection Failed',
        description: 'There was an error disconnecting your wallet. Please try again.',
        variant: 'destructive',
      })
    }
    onClose()
  }

  const { setBtcAddressType } = useSettings()
  const handleClickBtcWallet = ({ address }: { address: string }) => {
    setBtcAddressType(getAddressFormatFromAddress(address))
    selectedWalletInfo?.wallet?.switchWalletAddress?.(address, selectedWalletInfo?.network ?? '')
    queryClient.invalidateQueries({ queryKey: [TANSTACK_QUERY_KEY.WALLET.GET_BTC_WALLET_ADDRESS] })
    onClose()
  }

  const handleConnectAnotherWallet = () => {
    setShowWalletModal({
      show: true,
      token,
    })
    setOpen(false)
  }

  const isMd = useIsMd()
  const getNetworkLogo = useGetNetworkIcon()
  return (
    <Popover open={isOpen} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <div className='flex relative wallet-container'>
          <Button
            variant='secondary'
            size={isMd ? 'default' : 'sm'}
            className={cn('hover:bg-main-8 hover:ring-main-100', {
              'ring-main-100 bg-main-8': isOpen,
            })}
          >
            <RenderIf condition={isMd || connectedWallets.length < 3}>
              <CommonWallet className='size-6 flex-shrink-0' />
              {connectedWallets?.map(([_, wallet]) => (
                <img
                  key={wallet.networkInfo?.id}
                  src={getNetworkLogo(wallet.networkInfo?.networkId)}
                  alt={wallet.networkInfo?.networkLogoUri}
                  className='size-5 rounded-md'
                />
              ))}
            </RenderIf>
            <RenderIf condition={!isMd && connectedWallets.length > 2}>
              <img
                src={getNetworkLogo(wallets[connectedWallets[0][0]].networkInfo?.networkId)}
                alt={wallets[connectedWallets[0][0]].networkInfo?.networkLogoUri}
                className='size-5 rounded-md'
              />
              <div className='size-5 rounded-md flex items-center justify-center bg-main-100 text-size-[10px] font-[700]'>
                +{connectedWallets.length - 1}
              </div>
            </RenderIf>
            <CommonArrowDropDown
              className={cn('size-6 hidden md:block transition-transform duration-300 flex-shrink-0', {
                'rotate-180': isOpen,
                'text-main-100': isOpen,
              })}
            />
          </Button>
        </div>
      </PopoverTrigger>
      <PopoverContent className='min-w-80' align={isMd ? 'center' : 'end'}>
        <div className='flex flex-col pb-3'>
          <RenderIf condition={selectedWalletInfo}>
            <NavigationHeader
              className='px-4 py-3'
              isShowHome={false}
              title={t('button.BACK')}
              onBack={() => setSelectedWalletInfo(undefined)}
            />
            <div>
              {selectedWalletInfo?.wallet?.getAddresses?.()?.map((address) => {
                return (
                  <WalletBtcItem
                    key={address}
                    address={address}
                    wallet={selectedWalletInfo}
                    onClick={() => handleClickBtcWallet({ address })}
                  />
                )
              })}
            </div>
          </RenderIf>
          <RenderIf condition={!selectedWalletInfo}>
            {connectedWallets.map(([network, wallet]) => {
              return (
                <WalletItem
                  address={wallet.address}
                  setOpenSelectWallet={setSelectedWalletInfo}
                  key={wallet.address}
                  wallet={wallet}
                  onDisconnect={(e) => handleDisconnect(e, network)}
                  onCopy={handleCopy}
                />
              )
            })}
          </RenderIf>

          <RenderIf condition={!selectedWalletInfo}>
            <div className='px-4 py-2.5'>
              <Button variant='secondary' className='w-full' onClick={handleConnectAnotherWallet}>
                <CommonWallet className='size-6' />
                {t('button.CONNECT_ANOTHER_WALLET')}
              </Button>
            </div>
            <div className='px-4 '>
              <Button variant='secondary' className='w-full text-red-100' onClick={handleDisconnectAll}>
                <CommonLogoutNone className='size-6' />
                {t('button.DISCONNECT_WALLETS')}
              </Button>
            </div>
          </RenderIf>
        </div>
      </PopoverContent>
    </Popover>
  )
}
